<template>
  <div class="app-logo" :class="{ 'logo-small': small }">
    <div v-if="showLogo" class="logo-icon">
      <!-- Use your updated transparent logo -->
      <img
        :src="logoSrc"
        alt="KursKompass Logo"
        :width="logoSize"
        :height="logoSize"
        class="logo-image"
      />
    </div>

    <div v-if="showText" class="logo-text" :class="{ 'text-small': small }">
      <span class="brand-name">
        <span class="brand-kurs">kurs</span
        ><span class="brand-kompass">kompass.org</span>
      </span>
      <span v-if="!small" class="brand-tagline">{{ taglineText }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import logoImage from "@/assets/images/kurskompass-logo.png";

interface Props {
  small?: boolean;
  showText?: boolean;
  showLogo?: boolean;
  size?: number;
}

const props = withDefaults(defineProps<Props>(), {
  small: false,
  showText: true,
  showLogo: true,
  size: 40,
});

const { locale } = useI18n();

const logoSize = computed(() => {
  if (props.small) return props.size * 0.8;
  return props.size;
});

const logoSrc = computed(() => logoImage);

const taglineText = computed(() => {
  if (locale.value === "de") {
    return "Deine Kurs-Entscheidungshilfe";
  } else if (locale.value === "zh") {
    return "你的选课决策助手";
  } else {
    return "Your Course Decision Helper";
  }
});
</script>

<style scoped>
.app-logo {
  display: flex;
  align-items: center;
  gap: 15px; /* 进一步增加图标和文字之间的间距 */
  cursor: pointer;
  transition: all 0.3s ease;
}

.app-logo:hover {
  transform: scale(1.05);
}

.logo-small {
  gap: 12px; /* 小logo也进一步增加间距 */
}

.logo-small .brand-name {
  margin-top: 0;
}

.logo-small .logo-icon {
  align-self: center;
  margin-top: 4px;
}

.logo-small .brand-kurs {
  color: #667eea;
}

.logo-small .brand-kompass {
  color: #ffd700;
}

.logo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-image {
  object-fit: contain;
  transition: all 0.3s ease;
  /* Enhanced styling for transparent logo */
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15));
  background: transparent;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  transform: scale(1.4); /* 放大图像但不影响布局空间 */
}

.app-logo:hover .logo-image {
  filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.2));
  transform: scale(1.5); /* hover时再稍微放大一点 */
}

.logo-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: center;
  align-items: center;
}

.brand-name {
  font-size: 1.8rem;
  font-weight: 700;
  letter-spacing: -0.5px;
  text-transform: lowercase;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-top: -32px;
}

.brand-kurs {
  color: #ffffff;
}

.brand-kompass {
  color: #ffd700;
}

.brand-tagline {
  font-size: 0.85rem;
  color: #6b7280;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.text-small .brand-name {
  font-size: 1.4rem;
}

.text-small .brand-tagline {
  font-size: 0.75rem;
}

/* Large logo styles for hero section */
.app-logo:not(.logo-small) {
  gap: 0px;
}

.app-logo:not(.logo-small) .brand-name {
  font-size: 3.5rem;
  font-weight: 800;
}

.app-logo:not(.logo-small) .brand-tagline {
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffebb3;
  opacity: 0.9;
  margin-top: -2px;
  text-align: center;
}

/* Responsive */
@media (max-width: 768px) {
  .brand-tagline {
    display: none;
  }

  .brand-name {
    font-size: 1.5rem;
  }

  .logo-small .brand-name {
    font-size: 1.3rem;
  }
}

@media (max-width: 480px) {
  .app-logo {
    gap: 6px;
  }

  .brand-name {
    font-size: 1.4rem;
  }

  .logo-small .brand-name {
    font-size: 1.2rem;
  }
}

/* Animation for logo loading */
@keyframes logoFadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.app-logo {
  animation: logoFadeIn 0.6s ease-out;
}
</style>
